import React, { useEffect, useState, useRef } from 'react';
import { MessageCircle, X, Send, ExternalLink } from 'lucide-react';
interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}
export function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([{
    id: '1',
    text: "Hi! I'm the SkillzHQ assistant. How can I help you today?",
    sender: 'bot',
    timestamp: new Date()
  }]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  const faqResponses: Record<string, string> = {
    courses: 'We offer several courses including Executive Assistant Mastery, Virtual Sales, Employability Skills, and SmartStart Onboarding. Visit our Courses page to learn more!',
    pricing: 'Our Executive Assistant Mastery Course is ₦100,000 ($67) for the full 8-week program. We also offer special group rates for organizations.',
    duration: 'Most of our courses run for 6-8 weeks with a mix of self-paced learning and live sessions.',
    certificate: 'Yes! You receive a professional certificate upon successful completion of any course.',
    contact: 'You can reach us at support@myskillzhq.com or call 0810 683 6707.',
    about: 'SkillzHQ is an innovative management consulting and skills development firm focused on helping organizations align culture transformation with business growth.',
    payment: 'We accept payments via Paystack (cards, bank transfer, USSD). Payment is secure and processed instantly.',
    support: 'Our support team is available via email at support@myskillzhq.com. We typically respond within 24 hours.',
    enrollment: 'To enroll, visit any course page and click "Enroll Now". After payment, you\'ll fill out an enrollment form and receive access within 48 hours.',
    group: 'Yes! We offer special group rates for organizations, schools, and teams. Contact us at support@myskillzhq.com for custom pricing.'
  };
  const findBestResponse = (userMessage: string): string | null => {
    const lowerMessage = userMessage.toLowerCase();
    for (const [keyword, response] of Object.entries(faqResponses)) {
      if (lowerMessage.includes(keyword)) {
        return response;
      }
    }
    return null;
  };
  const handleSendMessage = () => {
    if (!inputValue.trim()) return;
    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);
    setTimeout(() => {
      const botResponse = findBestResponse(inputValue);
      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: botResponse || "I don't have a specific answer for that question. For personalized assistance, please email us at support@myskillzhq.com or fill out our contact form and we'll get back to you via email!",
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botMessage]);
      setIsTyping(false);
    }, 1000);
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  const quickQuestions = ['What courses do you offer?', 'How much does it cost?', 'Do I get a certificate?', 'How do I enroll?'];
  const handleQuickQuestion = (question: string) => {
    setInputValue(question);
  };
  return <>
      {/* Chat Button */}
      {!isOpen && <button onClick={() => setIsOpen(true)} className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-r from-blue-600 to-green-600 text-white rounded-full shadow-2xl hover:scale-110 transition-transform duration-300 flex items-center justify-center z-50 group" aria-label="Open chat" data-id="element-183">
          <MessageCircle className="w-7 h-7" data-id="element-184" />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse" data-id="element-185"></span>
        </button>}

      {/* Chat Window */}
      {isOpen && <div className="fixed bottom-6 right-6 w-96 h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col z-50 border border-gray-200" data-id="element-186">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white p-4 rounded-t-2xl flex items-center justify-between" data-id="element-187">
            <div className="flex items-center gap-3" data-id="element-188">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center" data-id="element-189">
                <MessageCircle className="w-6 h-6 text-blue-600" data-id="element-190" />
              </div>
              <div data-id="element-191">
                <h3 className="font-semibold" data-id="element-192">SkillzHQ Assistant</h3>
                <p className="text-xs text-blue-100" data-id="element-193">Always here to help</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 rounded-full p-2 transition-colors" aria-label="Close chat" data-id="element-194">
              <X className="w-5 h-5" data-id="element-195" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50" data-id="element-196">
            {messages.map(message => <div key={message.id} className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-197">
                <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${message.sender === 'user' ? 'bg-blue-600 text-white' : 'bg-white text-gray-800 border border-gray-200'}`} data-id="element-198">
                  <p className="text-sm leading-relaxed" data-id="element-199">{message.text}</p>
                  <p className={`text-xs mt-1 ${message.sender === 'user' ? 'text-blue-100' : 'text-gray-500'}`} data-id="element-200">
                    {message.timestamp.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })}
                  </p>
                </div>
              </div>)}

            {isTyping && <div className="flex justify-start" data-id="element-201">
                <div className="bg-white text-gray-800 border border-gray-200 rounded-2xl px-4 py-3" data-id="element-202">
                  <div className="flex gap-1" data-id="element-203">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" data-id="element-204"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                animationDelay: '0.2s'
              }} data-id="element-205"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                animationDelay: '0.4s'
              }} data-id="element-206"></div>
                  </div>
                </div>
              </div>}

            {/* Quick Questions */}
            {messages.length === 1 && <div className="space-y-2" data-id="element-207">
                <p className="text-xs text-gray-500 text-center" data-id="element-208">
                  Quick questions:
                </p>
                {quickQuestions.map((question, index) => <button key={index} onClick={() => handleQuickQuestion(question)} className="w-full text-left text-sm bg-white border border-gray-200 rounded-lg px-3 py-2 hover:border-blue-300 hover:bg-blue-50 transition-colors" data-id="element-209">
                    {question}
                  </button>)}
              </div>}

            <div ref={messagesEndRef} data-id="element-210" />
          </div>

          {/* Input Section */}
          <div className="p-4 border-t border-gray-200 bg-white rounded-b-2xl" data-id="element-211">
            {/* Tally Form Button */}
            <a href="https://tally.so/r/3Ep6aX" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-blue-600 to-green-600 text-white font-medium py-2.5 px-4 rounded-full hover:shadow-lg hover:scale-105 transition-all duration-300 mb-3" data-id="element-212">
              <ExternalLink className="w-4 h-4" data-id="element-213" />
              Fill Contact Form
            </a>

            <div className="flex gap-2 mb-2" data-id="element-214">
              <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyPress={handleKeyPress} placeholder="Type your message..." className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:border-blue-500 text-sm" data-id="element-215" />
              <button onClick={handleSendMessage} disabled={!inputValue.trim()} className="bg-blue-600 text-white rounded-full p-2 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" aria-label="Send message" data-id="element-216">
                <Send className="w-5 h-5" data-id="element-217" />
              </button>
            </div>
            <p className="text-xs text-gray-500 text-center" data-id="element-218">
              Need more help? Email{' '}
              <a href="mailto:support@myskillzhq.com" className="text-blue-600 hover:underline" data-id="element-219">
                support@myskillzhq.com
              </a>
            </p>
          </div>
        </div>}
    </>;
}